import React, { Children, Fragment, useState, useContext, useEffect } from 'react'
import { Icon } from '../html/icon'
import { Tabs, Tab } from '../html/tab'
import { Header } from '../html/header'
import { Page } from './page'
import { SiteContext } from './siteContext'
import './site.css'

import { ReactNotifications, Store } from 'react-notifications-component'
import 'react-notifications-component/dist/theme.css'



/**
 * Site Provider
 */
export const SiteProvider = ({ children, siteLang, siteDictionary, showConsole }) => {

    const [lang, setLang] = useState(siteLang)
    const [dictionary, setDictionary] = useState(siteDictionary)
    const [sideNav, setSideNav] = useState('max')
    const [showNav, setShowNav] = useState(false)
    const [info, setInfo] = useState(null)
    const [console, setConsole] = useState(false)
    const [page, setPage] = useState()
    const [dialog, setDialog] = useState()
    const [promptDialog, setPromptDialog] = useState()
    const [preview, setPreview] = useState()
    const [breadcrumb, setBreadcrumb] = useState()
    const [focused, setFocused] = useState()

    const value = {

        lang,
        setLang,

        dictionary,
        setDictionary,

        focused,
        changeFocus: (next) => {
            if (focused) focused.lose()
            setFocused(next)
        },
        clearFocus: () => {
            if (focused) focused.lose()
            setFocused(null)
        },

        sideNav,
        setSideNav,

        showNav,
        setShowNav,

        info,
        openInfo: (info) => { setInfo(info) },
        closeInfo: () => { setInfo(null) },

        console,
        toggleConsole: () => { setConsole(!console) },

        breadcrumb,
        setBreadcrumb,

        page,
        goto: (id) => { setPage(id) },

        dialog,
        openDialog: (dialog) => { setDialog(dialog) },
        closeDialog: () => { setDialog(null) },

        preview,
        openPreview: (preview) => { setPreview(preview) },
        closePreview: () => { setPreview(null) },

        confirm: (message) => window.confirm(message),
        prompt: (message) => window.prompt(message),

        promptDialog,
        openPromptDialog: (dialog) => {setPromptDialog(dialog)},
        closePromptDialog: () => { setPromptDialog(null)},

        notify: ({ title, body, type = "success" }) => {
            Store.addNotification({
                title,
                message: body,
                type,
                insert: "top",
                container: "top-right",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: {
                    duration: 3000,
                    onScreen: true
                }
            });
        }
    }

    return (
        <SiteContext.Provider value={value}>
            {children}
        </SiteContext.Provider>
    )
}

/**
 * Site
 */
export const Site = ({ icon, logo, title, toolbar, footer, children, init, min, lang, dictionary }) => {
    return (
        <SiteProvider siteLang={lang} siteDictionary={dictionary}>
            <div className="site6">
                <SiteHeader icon={icon} title={title} />
                <SiteToolBar >{toolbar}</SiteToolBar>
                <SiteMenu logo={logo} title={title} min={min} >{children}</SiteMenu>
                <SitePage init={init}>
                    {children}
                    <Page id="EMPTY">EMPTY</Page>
                </SitePage>
                <SiteAside />
                <SiteConsole />
                <SiteDialog />
                <SitePromptDialog />
                <SitePreview />
                <SiteNotifications />
                <SiteFooter>{footer}</SiteFooter>
            </div>
        </SiteProvider>
    )
}

const SiteNotifications = () => {
    return (
        <ReactNotifications />
    )
}

/**
 * Site Header
 */
const SiteHeader = (props) => {
    const { icon = "equalizer", title } = props
    return (
        <Header icon={icon} title={title} />
    )
}

/**
 * Site ToolBar
 */
const SiteToolBar = ({ children }) => {
    const context = useContext(SiteContext)
    const { breadcrumb } = context
    return (
        <nav>
            {children}
        </nav>
    )
}

/**
 * Site Footer
 */
 const SiteFooter = ({ children }) => {
    const context = useContext(SiteContext)
    const { breadcrumb } = context
    return (
        <footer>
            {children}
        </footer>
    )
}

/**
 * Site Aside
 */
const SiteAside = () => {
    const context = useContext(SiteContext)
    return context.info ? (
        <aside>
            {context.info}
        </aside>
    ) : ''
}

/**
 * SiteMenu
 */
const SiteMenu = ({ logo, title, children, min }) => {

    const context = useContext(SiteContext)
    const { page, sideNav, setSideNav, showNav } = context

    useEffect(() => {
        if (min) context.setSideNav('min')
    }, [])

    function toggle() {
        const next = sideNav === 'max' ? 'min' : 'max'
        setSideNav(next)
    }

    const goto = (id) => {
        context.setShowNav(false)
        context.goto(id)
    }

    const sections = children ?
        Children.toArray(children).reduce((sections, page) => {
            const section = page.props ? page.props.section : '...'
            if (!sections[section]) sections[section] = []
            const { id, icon, title } = page.props
            if (title) sections[section].push({ id, icon, title })
            return sections
        }, {}) : {}

    const style = sideNav === 'max' ? 'max' : 'min'
    const toggleIcon = sideNav === 'max' ? 'chevron_left' : 'chevron_right'
    const menutTitle = sideNav === 'max' ? title : ''
    return (
        <menu className={`${style} ${showNav ? 'show' : ''}`}>
            <Header title={menutTitle}/>
            <main >
                {Object.keys(sections).map(title => (
                    <Fragment key={title}>
                        <div className={`section-title ${style}`}>{sideNav === "max" ? title : ''}</div>
                        {sections[title].map(({ id, icon = 'info', title }) => {
                            const styleItem = id === page ? 'selected' : ''
                            return (
                                <div className={`site-menu-item ${styleItem}`} key={id} onClick={() => goto(id)}>
                                    <Icon key={id} icon={icon} clickable action={() => goto(id)} />
                                    {sideNav === 'max' ? <label>{title}</label> : null}
                                </div>
                            )
                        })}
                        <div className="section-divider" ></div>
                    </Fragment>
                ))}
            </main>
            <footer>
                <Icon icon={toggleIcon} clickable action={toggle} />
            </footer>
        </menu>
    )
}

/**
 * SitePage
 */
const SitePage = ({ children, init }) => {
    const context = useContext(SiteContext)
    const { page } = context
    useEffect(() => {
        if (init) {
            context.goto(init)
        } else {
            context.goto("EMPTY")
        }
    }, [])
    return (
        <main>
            {React.Children.toArray(children).filter(child => child.props ? child.props.id === page : false)}
        </main>
    )
}

/**
 * Site Dialog
 */
const SiteDialog = () => {
    const context = useContext(SiteContext)
    return context.dialog ? context.dialog : ''
}


/**
 * Site Promtp Dialog
 */
 const SitePromptDialog = () => {
    const context = useContext(SiteContext)
    return context.promptDialog ? context.promptDialog : ''
}

/**
 * Site Preview
 */
const SitePreview = () => {
    const context = useContext(SiteContext)
    return context.preview ? (
        <div className="site-preview" >
            {context.preview}
        </div>
    ) : ''
}

/**
 * Site Console
 */
const SiteConsole = () => {
    const context = useContext(SiteContext)
    return context.console ? (
        <footer className="site-console" >
            <Header>
                <Tabs>
                    <Tab label="Console" />
                </Tabs>
            </Header>
            <main>

            </main>
        </footer>
    ) : ''
}